/**
 * Cuentas por Cobrar — saldos pendientes por cliente, registro de abonos,
 * estado de cuenta y historial de pagos.
 */
import { useEffect, useState, useMemo } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, CreditCard, Receipt, ArrowDownToLine, Banknote, Smartphone, FileText, Ban, Wallet, Download, ChevronDown, ChevronRight, ArrowDownAZ, ArrowDown10 } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { PageLoader } from "@/components/Spinner";
import { WhatsAppShareButton } from "@/components/WhatsAppShare";
import { useSubscription } from "@/lib/subscription";

const METHODS = [
  { value: "efectivo", label: "Efectivo", Icon: Banknote },
  { value: "transferencia", label: "Transferencia", Icon: ArrowDownToLine },
  { value: "tarjeta", label: "Tarjeta", Icon: CreditCard },
  { value: "sinpe", label: "SINPE Móvil", Icon: Smartphone },
  { value: "cheque", label: "Cheque", Icon: FileText },
  { value: "otro", label: "Otro", Icon: FileText },
];

// Movement type styling for the timeline ledger.
const TIPO_META = {
  factura:       { label: "Factura",   color: "text-rose-700",    bg: "bg-rose-50",    ring: "border-rose-200" },
  pago:          { label: "Pago",      color: "text-emerald-700", bg: "bg-emerald-50", ring: "border-emerald-200" },
  pago_a_cuenta: { label: "A cuenta",  color: "text-sky-700",     bg: "bg-sky-50",     ring: "border-sky-200" },
  nota_credito:  { label: "NC",        color: "text-amber-700",   bg: "bg-amber-50",   ring: "border-amber-200" },
};

const formatDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("es-CR", { dateStyle: "short" }); }
  catch { return iso; }
};

export default function Receivables() {
  const { isAdmin } = useSubscription();
  const [tab, setTab] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ rows: [], summary: { total_pendiente: 0, clientes_con_deuda: 0, total_saldo_a_favor: 0 } });
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");
  // Sort mode for the per-customer accordion list.
  // 'alpha' = alphabetical by nombre (default per user request, Feb 2026)
  // 'balance' = legacy descending by total_pendiente.
  const [sortMode, setSortMode] = useState("alpha");
  // Map of cliente key (cliente_id || nombre_cliente) → { loading, data }
  // where `data` is the response from /receivables/statement/{id}.
  // The accordion lazy-loads each customer's movements + pending docs
  // on first expand so the page stays fast for tenants with hundreds
  // of receivables.
  const [expanded, setExpanded] = useState(() => new Set());
  const [details, setDetails] = useState(() => ({}));

  // Payment dialog
  const [payOpen, setPayOpen] = useState(false);
  const [paySaving, setPaySaving] = useState(false);
  const [pay, setPay] = useState({
    nombre_cliente: "",
    cliente_id: "",
    monto: "",
    metodo: "efectivo",
    referencia: "",
    nota: "",
    invoice_id: "",
  });
  // Pending invoices for the selected customer (so admin can pick one or N/A=a cuenta)
  const [clientInvoices, setClientInvoices] = useState([]);

  const fetchAll = async () => {
    try {
      const [sum, pays] = await Promise.all([
        api.get("/receivables/summary"),
        api.get("/payments"),
      ]);
      setSummary(sum.data);
      setPayments(pays.data);
    } catch { toast.error("Error al cargar Cuentas por Cobrar"); }
  };

  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const rows = !q
      ? summary.rows
      : summary.rows.filter(r => (r.nombre_cliente || "").toLowerCase().includes(q));
    const sorted = [...rows];
    if (sortMode === "alpha") {
      // Spanish locale comparison — case + accent insensitive ("José" sorts
      // next to "Jose"). Customer rows without a name fall to the bottom.
      sorted.sort((a, b) =>
        (a.nombre_cliente || "~").localeCompare(b.nombre_cliente || "~", "es", { sensitivity: "base" })
      );
    } else {
      // Legacy: biggest debt first.
      sorted.sort((a, b) => (b.total_pendiente || 0) - (a.total_pendiente || 0));
    }
    return sorted;
  }, [summary.rows, search, sortMode]);

  const filteredPayments = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return payments;
    return payments.filter(p =>
      (p.numero || "").toLowerCase().includes(q) ||
      (p.nombre_cliente || "").toLowerCase().includes(q) ||
      (p.referencia || "").toLowerCase().includes(q),
    );
  }, [payments, search]);

  // Toggle one customer row open/closed. On first open we fetch the full
  // statement (pending invoices + movements) for that customer.
  const toggleExpanded = async (row) => {
    const key = row.cliente_id || row.nombre_cliente;
    if (!key) return;
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (!details[key]) {
      setDetails(prev => ({ ...prev, [key]: { loading: true } }));
      try {
        const { data } = await api.get(`/receivables/statement/${encodeURIComponent(key)}`);
        setDetails(prev => ({ ...prev, [key]: { loading: false, data } }));
      } catch {
        setDetails(prev => ({ ...prev, [key]: { loading: false, error: true } }));
        toast.error("Error al cargar movimientos");
      }
    }
  };

  // Force-refresh a single customer's expanded details — used after
  // registering a new payment so the user sees fresh balance + movs
  // without reloading the whole page.
  const refreshExpanded = async (key) => {
    if (!key) return;
    try {
      const { data } = await api.get(`/receivables/statement/${encodeURIComponent(key)}`);
      setDetails(prev => ({ ...prev, [key]: { loading: false, data } }));
    } catch { /* ignore */ }
  };

  const openPayDialog = async (row = null) => {
    setPay({
      nombre_cliente: row?.nombre_cliente || "",
      cliente_id: row?.cliente_id || "",
      monto: "",
      metodo: "efectivo",
      referencia: "",
      nota: "",
      invoice_id: "",
    });
    setPayOpen(true);
    if (row?.cliente_id || row?.nombre_cliente) {
      try {
        const q = row.cliente_id ? `?cliente_id=${encodeURIComponent(row.cliente_id)}` : "";
        const { data } = await api.get(`/receivables/by-invoice${q}`);
        const filt = row.cliente_id
          ? data
          : data.filter(d => d.nombre_cliente === row.nombre_cliente);
        setClientInvoices(filt);
      } catch { setClientInvoices([]); }
    } else {
      setClientInvoices([]);
    }
  };

  const submitPayment = async (e) => {
    e?.preventDefault();
    const monto = parseFloat(pay.monto);
    if (!monto || monto <= 0) { toast.error("Ingresá un monto válido"); return; }
    if (!pay.nombre_cliente && !pay.cliente_id) { toast.error("Selecciona un cliente"); return; }
    setPaySaving(true);
    try {
      const body = {
        cliente_id: pay.cliente_id || "",
        nombre_cliente: pay.nombre_cliente || "",
        monto,
        metodo: pay.metodo,
        referencia: pay.referencia || "",
        nota: pay.nota || "",
        invoice_id: pay.invoice_id || "",
      };
      const { data } = await api.post("/payments", body);
      const aplCount = data.aplicaciones?.length || 0;
      let msg = `Pago ${data.numero} registrado`;
      if (aplCount > 0) msg += ` — ${aplCount} factura(s) abonada(s)`;
      if (data.saldo_a_cuenta > 0) msg += ` · queda ¢${data.saldo_a_cuenta} a cuenta`;
      toast.success(msg);
      setPayOpen(false);
      fetchAll();
      // Also refresh the expanded panel for this customer so the chofer
      // / admin sees the new movement + reduced balance immediately.
      const key = pay.cliente_id || pay.nombre_cliente;
      if (key && expanded.has(key)) refreshExpanded(key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar pago");
    } finally {
      setPaySaving(false);
    }
  };

  const anularPayment = async (p) => {
    if (!window.confirm(`¿Anular pago ${p.numero}? Se devolverá el saldo a las facturas.`)) return;
    try {
      await api.post(`/payments/${p.id}/anular`);
      toast.success("Pago anulado");
      fetchAll();
      const key = p.cliente_id || p.nombre_cliente;
      if (key && expanded.has(key)) refreshExpanded(key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al anular pago");
    }
  };

  // Statement PDF + print helpers — call /receivables/statement/{id}/pdf
  // with the auth header (HTML <a download> can't send custom headers).
  const fetchStatementBlob = async (cliente) => {
    const id = cliente.cliente_id || cliente.nombre_cliente;
    const token = localStorage.getItem("token");
    const resp = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/receivables/statement/${encodeURIComponent(id)}/pdf`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return resp.blob();
  };

  const downloadStatement = async (cliente) => {
    try {
      const blob = await fetchStatementBlob(cliente);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `estado-${(cliente.nombre_cliente || "cliente").replace(/[^a-z0-9]/gi, "_")}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch { toast.error("Error al descargar PDF"); }
  };

  const printStatement = async (cliente) => {
    try {
      const blob = await fetchStatementBlob(cliente);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (win) win.addEventListener("load", () => win.print(), { once: true });
    } catch { toast.error("No se pudo abrir la impresión"); }
  };

  if (loading) return <PageLoader label="Cargando CxC…" testid="receivables-loader" />;

  return (
    <div data-testid="receivables-page">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Cuentas por Cobrar
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin
              ? "Saldos pendientes de cobro, abonos por cliente y estado de cuenta. Cada factura emitida queda como cobro pendiente hasta que se abone."
              : "Modo consulta · Ves los saldos y movimientos de tus clientes asignados. Solo el administrador puede registrar o anular abonos."}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => openPayDialog(null)} className="btn-primary rounded-sm" data-testid="receivables-new-payment-button">
            <Wallet className="h-4 w-4 mr-2" />Registrar abono
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
        <Card className="bg-white border border-rose-200 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">TOTAL POR COBRAR</p>
            <p className="text-2xl font-black text-rose-700" data-testid="cxc-total-pendiente">{formatMoney(summary.summary.total_pendiente)}</p>
          </div>
          <Receipt className="h-6 w-6 text-rose-600" />
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">CLIENTES CON DEUDA</p>
            <p className="text-2xl font-black">{summary.summary.clientes_con_deuda}</p>
          </div>
          <CreditCard className="h-6 w-6 text-[rgb(0,47,167)]" />
        </Card>
        <Card className="bg-white border border-emerald-200 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">SALDOS A FAVOR (NC)</p>
            <p className="text-2xl font-black text-emerald-700">{formatMoney(summary.summary.total_saldo_a_favor)}</p>
          </div>
          <ArrowDownToLine className="h-6 w-6 text-emerald-600" />
        </Card>
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {[
          { key: "summary",  label: "Por cliente" },
          { key: "payments", label: `Historial de pagos (${payments.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? "border-[rgb(0,47,167)] text-[rgb(0,47,167)]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`receivables-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === "summary" ? "Buscar por cliente…" : "Buscar por número, cliente o referencia…"} className="rounded-sm pl-9" data-testid="receivables-search" />
          </div>
          {tab === "summary" && (
            <Select value={sortMode} onValueChange={setSortMode}>
              <SelectTrigger className="rounded-sm w-[200px]" data-testid="receivables-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alpha">
                  <div className="flex items-center gap-1.5"><ArrowDownAZ className="h-3.5 w-3.5" />Alfabético (A–Z)</div>
                </SelectItem>
                <SelectItem value="balance">
                  <div className="flex items-center gap-1.5"><ArrowDown10 className="h-3.5 w-3.5" />Mayor deuda primero</div>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        {tab === "summary" ? (
          <div className="space-y-2" data-testid="cxc-customer-accordion">
            {filtered.length === 0 ? (
              <p className="py-10 text-center text-slate-400 text-sm">No hay clientes con saldo pendiente. ¡Bien!</p>
            ) : filtered.map((r, i) => {
              const key = r.cliente_id || r.nombre_cliente;
              const isOpen = expanded.has(key);
              const det = details[key];
              return (
                <div key={key || i} className="border border-slate-200 rounded-sm overflow-hidden bg-white" data-testid={`cxc-row-${i}`}>
                  {/* Collapsed header — always visible. Click anywhere to toggle. */}
                  <button
                    type="button"
                    onClick={() => toggleExpanded(r)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
                    data-testid={`cxc-row-toggle-${i}`}
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{r.nombre_cliente}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {r.facturas_count} {r.facturas_count === 1 ? "documento" : "documentos"} pendiente{r.facturas_count === 1 ? "" : "s"}
                          {r.oldest_invoice_date && <> · más antiguo: {formatDate(r.oldest_invoice_date)}</>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      {r.saldo_a_favor > 0 && (
                        <div className="text-right hidden sm:block">
                          <p className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold">A favor</p>
                          <p className="mono font-bold text-emerald-700 text-sm" data-testid={`cxc-row-favor-${i}`}>{formatMoney(r.saldo_a_favor)}</p>
                        </div>
                      )}
                      <div className="text-right">
                        <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Balance a hoy</p>
                        <p className={`mono font-bold text-base ${r.total_pendiente > 0 ? "text-rose-700" : "text-slate-500"}`} data-testid={`cxc-row-balance-${i}`}>
                          {formatMoney(r.total_pendiente)}
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* Expanded panel — 3 sub-sections per the user's request. */}
                  {isOpen && (
                    <div className="border-t border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid={`cxc-row-detail-${i}`}>
                      {/* Quick actions */}
                      <div className="flex flex-wrap gap-2 justify-end">
                        {r.cliente_id && (
                          <>
                            <WhatsAppShareButton type="statement" id={r.cliente_id} label="Estado de cuenta" testid={`cxc-detail-wa-share-${i}`} />
                            <WhatsAppShareButton type="payment_reminder" id={r.cliente_id} label="Recordar pago" testid={`cxc-detail-wa-reminder-${i}`} />
                          </>
                        )}
                        <Button type="button" variant="outline" size="sm" onClick={() => printStatement(r)} className="rounded-sm h-8" data-testid={`cxc-detail-print-${i}`}>
                          <FileText className="h-3.5 w-3.5 mr-1.5" />Imprimir
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => downloadStatement(r)} className="rounded-sm h-8" data-testid={`cxc-detail-pdf-${i}`}>
                          <Download className="h-3.5 w-3.5 mr-1.5" />PDF
                        </Button>
                        {isAdmin && r.total_pendiente > 0 && (
                          <Button size="sm" className="btn-primary rounded-sm h-8" onClick={() => openPayDialog(r)} data-testid={`cxc-detail-pay-${i}`}>
                            <Wallet className="h-3.5 w-3.5 mr-1.5" />Abonar
                          </Button>
                        )}
                      </div>

                      {det?.loading || !det ? (
                        <p className="py-4 text-center text-xs text-slate-400">Cargando movimientos…</p>
                      ) : det?.error ? (
                        <p className="py-4 text-center text-xs text-rose-600">No se pudieron cargar los datos. <button className="underline" onClick={() => toggleExpanded(r)}>Reintentar</button></p>
                      ) : (
                        <>
                          {/* Balance + totals strip */}
                          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 text-xs">
                            <TotKpi label="Facturado" value={det.data.totales.total_facturado} />
                            <TotKpi label="Pagado" value={det.data.totales.total_pagado} />
                            <TotKpi label="Devoluciones" value={det.data.totales.total_devolucion} />
                            <TotKpi label="Pendiente" value={det.data.totales.saldo_pendiente} highlight="rose" />
                            <TotKpi label="A favor" value={det.data.totales.saldo_a_favor} highlight="emerald" />
                          </div>

                          {/* Documentos pendientes por pagar — invoices with saldo_pendiente > 0 */}
                          <PendingDocsTable invoices={det.data.facturas} testid={`cxc-detail-pending-${i}`} />

                          {/* Movimientos — full chronological timeline with running balance */}
                          <MovementsTable movs={det.data.movimientos} testid={`cxc-detail-movs-${i}`} />
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Número</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Fecha</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Cliente</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Método</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Ref.</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Monto</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">A cuenta</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map(p => {
                  const meta = METHODS.find(m => m.value === p.metodo) || METHODS[0];
                  const Icon = meta.Icon;
                  return (
                    <tr key={p.id} className="border-b border-slate-100" data-testid={`payment-row-${p.numero}`}>
                      <td className="py-2 px-3 mono font-medium">{p.numero}</td>
                      <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{formatDate(p.fecha)}</td>
                      <td className="py-2 px-3">{p.nombre_cliente}</td>
                      <td className="py-2 px-3"><span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 border border-slate-200"><Icon className="h-3 w-3" />{meta.label}</span></td>
                      <td className="py-2 px-3 mono text-xs text-slate-600">{p.referencia || "-"}</td>
                      <td className="py-2 px-3 text-right font-bold mono">{formatMoney(p.monto)}</td>
                      <td className="py-2 px-3 text-right mono">{p.saldo_a_cuenta > 0 ? <span className="text-emerald-700">{formatMoney(p.saldo_a_cuenta)}</span> : "-"}</td>
                      <td className="py-2 px-3 text-right">
                        {isAdmin ? (
                          <Button size="sm" variant="ghost" className="rounded-sm h-7 text-rose-600 hover:bg-rose-50" onClick={() => anularPayment(p)} data-testid={`payment-anular-${p.numero}`}>
                            <Ban className="h-3 w-3 mr-1" />Anular
                          </Button>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                    </tr>
                  );
                })}
                {filteredPayments.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-400">Sin pagos registrados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader><DialogTitle>Registrar abono</DialogTitle></DialogHeader>
          <form onSubmit={submitPayment} className="space-y-3 mt-2">
            <div>
              <label className="block text-xs font-medium mb-1">Cliente *</label>
              <Input value={pay.nombre_cliente} onChange={(e) => setPay({ ...pay, nombre_cliente: e.target.value })} placeholder="Nombre del cliente" className="rounded-sm" data-testid="payment-customer-input" />
              {pay.cliente_id && <p className="text-[10px] text-slate-500 mt-1 mono">id: {pay.cliente_id}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Monto *</label>
                <Input type="number" min="0" step="any" value={pay.monto} onChange={(e) => setPay({ ...pay, monto: e.target.value })} className="rounded-sm" data-testid="payment-amount-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Método *</label>
                <Select value={pay.metodo} onValueChange={(v) => setPay({ ...pay, metodo: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}><div className="flex items-center gap-1.5"><m.Icon className="h-3 w-3" />{m.label}</div></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Factura a la que se aplica</label>
              <Select value={pay.invoice_id || "__auto__"} onValueChange={(v) => setPay({ ...pay, invoice_id: v === "__auto__" ? "" : v })}>
                <SelectTrigger className="rounded-sm" data-testid="payment-invoice-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">A cuenta (aplica a la más antigua automáticamente)</SelectItem>
                  {clientInvoices.map(inv => (
                    <SelectItem key={inv.id} value={inv.id}>
                      <span className="mono">{inv.numero}</span> · {inv.fecha} · pendiente {formatMoney(inv.saldo_pendiente)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Referencia</label>
                <Input value={pay.referencia} onChange={(e) => setPay({ ...pay, referencia: e.target.value })} placeholder="N° cheque, transacción…" className="rounded-sm" data-testid="payment-reference-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Nota</label>
                <Input value={pay.nota} onChange={(e) => setPay({ ...pay, nota: e.target.value })} className="rounded-sm" data-testid="payment-note-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setPayOpen(false)} className="rounded-sm" disabled={paySaving}>Cancelar</Button>
              <Button type="submit" className="btn-primary rounded-sm" disabled={paySaving} data-testid="payment-submit-button">
                <Wallet className="h-4 w-4 mr-2" />{paySaving ? "Guardando…" : "Registrar abono"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PendingDocsTable({ invoices, testid }) {
  // Render every NON-anulada invoice that still has saldo_pendiente > 0.
  // Sorted oldest first so the chofer / collector sees what to chase.
  const pending = (invoices || [])
    .filter(inv => inv.estado !== "anulada" && Number(inv.saldo_pendiente || 0) > 0)
    .sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
  return (
    <div className="border border-slate-200 rounded-sm bg-white">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
        <Receipt className="h-3.5 w-3.5 text-rose-700" />
        <h4 className="text-xs uppercase tracking-wider font-bold text-slate-700">Documentos pendientes por pagar</h4>
        <span className="ml-auto text-[10px] text-slate-500">{pending.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid={testid}>
          <thead className="bg-white">
            <tr>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">N° Factura</th>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Fecha</th>
              <th className="text-right py-1.5 px-2 font-medium text-slate-600">Total</th>
              <th className="text-right py-1.5 px-2 font-medium text-slate-600">Abonado</th>
              <th className="text-right py-1.5 px-2 font-medium text-slate-600">Pendiente</th>
            </tr>
          </thead>
          <tbody>
            {pending.length === 0 ? (
              <tr><td colSpan={5} className="py-3 px-2 text-center text-slate-400 italic">Sin documentos pendientes — todas las facturas están al día.</td></tr>
            ) : pending.map((inv, i) => {
              const total = Number(inv.total || 0);
              const saldo = Number(inv.saldo_pendiente || 0);
              const abonado = Math.max(0, total - saldo);
              return (
                <tr key={inv.id || i} className="border-t border-slate-100">
                  <td className="py-1.5 px-2 mono font-medium">{inv.numero}</td>
                  <td className="py-1.5 px-2 text-slate-600 whitespace-nowrap">{(inv.fecha || "").slice(0, 10) || "-"}</td>
                  <td className="py-1.5 px-2 text-right mono">{formatMoney(total)}</td>
                  <td className="py-1.5 px-2 text-right mono text-emerald-700">{abonado > 0 ? formatMoney(abonado) : "-"}</td>
                  <td className="py-1.5 px-2 text-right mono font-bold text-rose-700">{formatMoney(saldo)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MovementsTable({ movs, testid }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-white">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
        <CreditCard className="h-3.5 w-3.5 text-[rgb(0,47,167)]" />
        <h4 className="text-xs uppercase tracking-wider font-bold text-slate-700">Movimientos de la cuenta</h4>
        <span className="ml-auto text-[10px] text-slate-500">{(movs || []).length}</span>
      </div>
      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
        <table className="w-full text-xs" data-testid={testid}>
          <thead className="bg-white sticky top-0">
            <tr>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Fecha</th>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Tipo</th>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">N°</th>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Descripción</th>
              <th className="text-right py-1.5 px-2 font-medium text-slate-600">Debe</th>
              <th className="text-right py-1.5 px-2 font-medium text-slate-600">Haber</th>
              <th className="text-right py-1.5 px-2 font-medium text-slate-600">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {(movs || []).length === 0 ? (
              <tr><td colSpan={7} className="py-4 text-center text-slate-400 italic">Sin movimientos registrados.</td></tr>
            ) : movs.map((m, i) => {
              const meta = TIPO_META[m.tipo] || TIPO_META.factura;
              return (
                <tr key={i} className={`border-t border-slate-100 ${m.tipo === "factura" ? "bg-white" : "bg-emerald-50/30"}`}>
                  <td className="py-1.5 px-2 text-slate-600 whitespace-nowrap">{formatDate(m.fecha)}</td>
                  <td className="py-1.5 px-2"><span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${meta.bg} ${meta.color} ${meta.ring}`}>{meta.label}</span></td>
                  <td className="py-1.5 px-2 mono">{m.numero}</td>
                  <td className="py-1.5 px-2 text-slate-700">{m.descripcion}</td>
                  <td className="py-1.5 px-2 text-right mono">{m.debe > 0 ? formatMoney(m.debe) : "-"}</td>
                  <td className="py-1.5 px-2 text-right mono text-emerald-700">{m.haber > 0 ? formatMoney(m.haber) : "-"}</td>
                  <td className={`py-1.5 px-2 text-right mono font-bold ${m.saldo > 0 ? "text-rose-700" : m.saldo < 0 ? "text-emerald-700" : "text-slate-400"}`}>{formatMoney(m.saldo)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TotKpi({ label, value, highlight }) {
  const colorMap = {
    rose: "text-rose-700 border-rose-200 bg-rose-50",
    emerald: "text-emerald-700 border-emerald-200 bg-emerald-50",
  };
  const cls = colorMap[highlight] || "text-slate-700 border-slate-200 bg-slate-50";
  return (
    <div className={`border rounded-sm px-2.5 py-1.5 ${cls}`}>
      <p className="text-[9px] uppercase tracking-wider font-bold opacity-70 leading-3">{label}</p>
      <p className="font-bold mono text-sm leading-4 mt-0.5">{(value || 0).toLocaleString("es-CR", { style: "currency", currency: "CRC", minimumFractionDigits: 0 })}</p>
    </div>
  );
}
